import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Mail, Phone, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+91',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const countryCodes = [
    { code: '+91', country: 'India' },
    { code: '+971', country: 'UAE' },
    { code: '+1', country: 'USA/Canada' },
    { code: '+44', country: 'UK' },
    { code: '+61', country: 'Australia' },
    { code: '+65', country: 'Singapore' },
    { code: '+966', country: 'Saudi Arabia' },
    { code: '+974', country: 'Qatar' },
    { code: '+968', country: 'Oman' },
    { code: '+973', country: 'Bahrain' },
    { code: '+965', country: 'Kuwait' },
    { code: '+92', country: 'Pakistan' },
    { code: '+880', country: 'Bangladesh' },
    { code: '+94', country: 'Sri Lanka' },
    { code: '+977', country: 'Nepal' },
    { code: '+60', country: 'Malaysia' },
    { code: '+63', country: 'Philippines' },
    { code: '+86', country: 'China' },
    { code: '+81', country: 'Japan' },
    { code: '+82', country: 'South Korea' }
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Combine country code with phone number
      const submissionData = {
        name: formData.name,
        email: formData.email,
        phone: `${formData.countryCode} ${formData.phone}`,
        message: formData.message
      };
      
      const response = await axios.post(`${API}/contact`, submissionData);
      
      if (response.status === 200) {
        toast.success('Thank you! Your message has been sent successfully. We will get back to you soon.');
        setFormData({ name: '', email: '', countryCode: '+91', phone: '', message: '' });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Sorry, there was an error sending your message. Please try again or email us directly at connect@dreambigeducation.co');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="contact-section">
      <div className="contact-container">
        <div className="contact-header">
          <h2 className="section-title">Have questions? Let's talk.</h2>
        </div>

        <div className="contact-content">
          <form onSubmit={handleSubmit} className="contact-form">
            <div className="form-group">
              <label htmlFor="name">Full Name *</label>
              <Input
                id="name"
                name="name"
                type="text"
                placeholder="Enter your full name"
                value={formData.name}
                onChange={handleChange}
                required
                disabled={isSubmitting}
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email Address *</label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="your.email@example.com"
                value={formData.email}
                onChange={handleChange}
                required
                disabled={isSubmitting}
              />
            </div>

            <div className="form-group">
              <label htmlFor="phone">Phone Number *</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <select
                  name="countryCode"
                  value={formData.countryCode}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  style={{
                    padding: '0.5rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                    color: '#022d66',
                    backgroundColor: 'white',
                    minWidth: '120px'
                  }}
                >
                  {countryCodes.map((item) => (
                    <option key={item.code} value={item.code}>
                      {item.code} {item.country}
                    </option>
                  ))}
                </select>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="Enter phone number"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                  style={{ flex: 1 }}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="message">Your Message</label>
              <Textarea
                id="message"
                name="message"
                placeholder="Tell us about your study abroad goals..."
                value={formData.message}
                onChange={handleChange}
                rows={4}
                disabled={isSubmitting}
              />
            </div>

            <Button type="submit" className="submit-button" disabled={isSubmitting}>
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </Button>
          </form>

          <div className="contact-right-column">
            <div className="contact-illustration-wrapper">
              <img 
                src="https://customer-assets.emergentagent.com/job_dubai-education/artifacts/qo7cemzx_Study%20abroad.gif" 
                alt="Student preparing to study abroad with passport and travel documents - contact Dream Big Education for counseling"
                className="contact-illustration"
              />
            </div>

            <div className="contact-info">
              <div className="contact-info-card">
                <div className="contact-icon">
                  <Mail size={24} />
                </div>
                <div>
                  <h3>Email Us</h3>
                  <a href="mailto:connect@dreambigeducation.co">connect@dreambigeducation.co</a>
                </div>
              </div>

              <div className="contact-info-card">
                <div className="contact-icon">
                  <Phone size={24} />
                </div>
                <div>
                  <h3>Call Us</h3>
                  <a href="tel:+919321007830">+91 9321 007 830</a>
                </div>
              </div>

              <div className="contact-info-card">
                <div className="contact-icon">
                  <MapPin size={24} />
                </div>
                <div>
                  <h3>Location</h3>
                  <p>Mumbai, India</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;